import React, { useEffect, useRef, useState } from "react";
import styles from "../Advancetrees.module.css";

interface HeapNode {
  id: number;
  value: number;
  x?: number;
  y?: number;
  left?: HeapNode | null;
  right?: HeapNode | null;
}

type Traversal = "preorder" | "inorder" | "postorder" | "levelorder";

interface MinHeapEngineProps {
  heapData: number[];
  traversal: Traversal | null;
  setHighlightLine: (line: number | null) => void;
  highlightedNodes: number[];
  setHighlightedNodes: React.Dispatch<React.SetStateAction<number[]>>;
  resetTrigger: boolean;
  generateTrigger: number;
  setActiveLine: (line: number | null) => void;
  setTraversalResult: React.Dispatch<React.SetStateAction<number[]>>;
}

const MinHeapEngine: React.FC<MinHeapEngineProps> = ({
  heapData,
  traversal,
  setHighlightLine,
  highlightedNodes,
  setHighlightedNodes,
  resetTrigger,
  generateTrigger,
  setActiveLine,
  setTraversalResult,
}) => {
  const [root, setRoot] = useState<HeapNode | null>(null);
  const animationTimers = useRef<NodeJS.Timeout[]>([]);

  const buildHeap = (arr: number[]): HeapNode | null => {
    if (arr.length === 0) return null;

    const nodes: HeapNode[] = arr.map((val, i) => ({
      id: i,
      value: val,
      left: null,
      right: null,
    }));

    for (let i = 0; i < nodes.length; i++) {
      const L = 2 * i + 1;
      const R = 2 * i + 2;
      if (L < nodes.length) nodes[i].left = nodes[L];
      if (R < nodes.length) nodes[i].right = nodes[R];
    }
    assignPositions(nodes[0]);
    return nodes[0];
  };

  const assignPositions = (
    node: HeapNode | null,
    depth = 0,
    minX = -400,
    maxX = 400,
    levelHeight = 100
  ) => {
    if (!node) return;
    const midX = (minX + maxX) / 2;
    const y = 80 + depth * levelHeight;
    node.x = midX;
    node.y = y;
    assignPositions(node.left ?? null, depth + 1, minX, midX, levelHeight);
    assignPositions(node.right ?? null, depth + 1, midX, maxX, levelHeight);
  };

  const heapify = (arr: number[]) => {
    const n = arr.length;
    const down = (i: number) => {
      let smallest = i;
      const L = 2 * i + 1;
      const R = 2 * i + 2;
      if (L < n && arr[L] < arr[smallest]) smallest = L;
      if (R < n && arr[R] < arr[smallest]) smallest = R;
      if (smallest !== i) {
        [arr[i], arr[smallest]] = [arr[smallest], arr[i]];
        down(smallest);
      }
    };
    for (let i = Math.floor(n / 2) - 1; i >= 0; i--) down(i);
    return arr;
  };

  useEffect(() => {
    if (heapData.length > 0) {
      const h = heapify([...heapData]);
      const r = buildHeap(h);
      setRoot(r);
    } else {
      setRoot(null);
    }
  }, [heapData, generateTrigger]);

  useEffect(() => {
    animationTimers.current.forEach(clearTimeout);
    animationTimers.current = [];
    setHighlightedNodes([]);
    setHighlightLine(null);
    setActiveLine(null);
    setTraversalResult([]);
  }, [resetTrigger]);

  useEffect(() => {
    if (!root || !traversal) return;  
    animationTimers.current.forEach(clearTimeout);
    animationTimers.current = [];
    setHighlightedNodes([]);
    setHighlightLine(null);
    setActiveLine(null);
    setTraversalResult([]);

    const order: HeapNode[] = [];

    const preorder = (node: HeapNode | null) => {
      if (!node) return;
      order.push(node);
      preorder(node.left ?? null);
      preorder(node.right ?? null);
    };

    const inorder = (node: HeapNode | null) => {
      if (!node) return;
      inorder(node.left ?? null);
      order.push(node);
      inorder(node.right ?? null);
    };

    const postorder = (node: HeapNode | null) => {
      if (!node) return;
      postorder(node.left ?? null);
      postorder(node.right ?? null);
      order.push(node);
    };

    const levelorder = (node: HeapNode | null) => {
      if (!node) return;
      const q: HeapNode[] = [node];
      while (q.length) {
        const cur = q.shift()!;
        order.push(cur);
        if (cur.left) q.push(cur.left);
        if (cur.right) q.push(cur.right);
      }
    };

    switch (traversal) {
      case "preorder":
        preorder(root);
        break;
      case "inorder":
        inorder(root);
        break;
      case "postorder":
        postorder(root);
        break;
      case "levelorder":
        levelorder(root);
        break;
    }

    const lineSeqMap: Record<Traversal, number[]> = {
      preorder: [2, 3, 4],   
      inorder: [2, 3, 4],    
      postorder: [2, 3, 4],  
      levelorder: [4, 5, 6, 7], 
    };
    const seq = lineSeqMap[traversal];
    
    const STEP_MS = 700;

    order.forEach((node, idx) => {
      const t = setTimeout(() => {
        setHighlightedNodes((prev) => [...prev, node.value]);
        setHighlightLine(idx % 5);
        setActiveLine(seq[idx % seq.length]);
        setTraversalResult((prev) => [...prev, node.value]);
      }, idx * STEP_MS);
      animationTimers.current.push(t);
    });

    const end = setTimeout(() => {
      setHighlightLine(null);
      setActiveLine(null);
    }, order.length * STEP_MS + 400);
    animationTimers.current.push(end);

    return () => {
      animationTimers.current.forEach(clearTimeout);
      animationTimers.current = [];
    };
  }, [traversal, root]);

  const nodes: HeapNode[] = [];
  const lines: { x1: number; y1: number; x2: number; y2: number; key: string }[] = [];

  const walk = (n: HeapNode | null) => {
    if (!n) return;
    nodes.push(n);
    if (n.left) {
      lines.push({
        x1: n.x!,
        y1: n.y!,
        x2: n.left.x!,
        y2: n.left.y!,
        key: `${n.id}-${n.left.id}`,
      });
      walk(n.left);
    }
    if (n.right) {
      lines.push({
        x1: n.x!,
        y1: n.y!,
        x2: n.right.x!,
        y2: n.right.y!,
        key: `${n.id}-${n.right.id}`,
      });
      walk(n.right);
    }
  };
  walk(root);

  const getDepth = (n: HeapNode | null): number =>
    !n ? 0 : 1 + Math.max(getDepth(n.left ?? null), getDepth(n.right ?? null));

  const depth = getDepth(root);
  const svgHeight = Math.max(450, depth * 120);
  const viewBoxHeight = Math.max(400, depth * 120);

  return (
    <div className={styles.heapViz}>
      <svg width="100%" height={svgHeight} viewBox={`-600 0 1200 ${viewBoxHeight}`}>
        {lines.map((L) => (
          <line
            key={L.key}
            x1={L.x1}
            y1={L.y1}
            x2={L.x2}
            y2={L.y2}
            className={styles.heapLine}
          />
        ))}

        {nodes.map((n) => (
          <g key={n.id}>
            <circle
              cx={n.x}
              cy={n.y}
              r="25"
              className={`${styles.heapNode} ${
                highlightedNodes.includes(n.value) ? styles.highlighted : ""
              }`}
            />
            <text x={n.x} y={n.y! + 5} className={styles.nodeLabel}>
              {n.value}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
};

export default MinHeapEngine;
